import { Fragment } from 'react';
import type { VideoEditorEditElements } from '../../types/videoEditor';

// ── Cartões dos elementos de edição, em UM lugar ────────────────────────────
//
// Este painel é mostrado em dois pontos que precisam concordar: o EDITOR de vídeo
// (onde a escolha é reeditável depois de gerado) e o passo a passo de PRODUÇÃO
// (onde ela é feita antes de pagar os créditos). Ele morava dentro do
// VideoProjectEditor e foi movido para cá quando o segundo ponto apareceu — a tela
// de criação (VideoEditorView) já tinha uma réplica dele, e a lição registrada
// naquele caso vale aqui: as duas cópias divergiram em quatro pontos antes de
// alguém notar. Uma terceira não seria criada.
//
// As miniaturas são o recurso central e não enfeite: é o que separa "tem uma
// caixinha marcada" de "eu sei o que vai acontecer no meu vídeo".

// A PAUSA existe só na produção (ver o cartão), então ela entra por um tipo que
// estende os cartões do editor em vez de sujar `VideoEditorEditElements` — o
// projeto salvo não tem esse campo e não deve passar a ter.
export type VideoEditorEditElementsChoice = VideoEditorEditElements & { pauses?: boolean };

function FieldLabel({ children, claro = false }: { children: React.ReactNode; claro?: boolean }) {
  return (
    <label className={`mb-1 block text-[10px] font-black uppercase tracking-[0.14em] ${claro ? 'text-slate-500' : 'text-slate-500'}`}>
      {children}
    </label>
  );
}

// Elementos de edição do PROJETO. Antes eles só existiam na tela de criação
// (VideoEditorView) e eram consumidos uma única vez pelo backend: depois de gerado
// o vídeo, a decisão ficava congelada — não dava para ligar o flash que a pessoa
// desligou nem desligar o zoom que atrapalhou. Agora o campo vive no projeto e
// escreve por `commit`, então o desfazer cobre.
//
// As miniaturas são as mesmas da tela de criação (é o que separa "tem uma caixinha
// marcada" de "eu sei o que vai acontecer no meu vídeo"), repintadas para o fundo
// escuro do editor.
export const EDIT_ELEMENTS_DEFAULT: VideoEditorEditElements = {
  zoomCuts: true,
  zoomAuto: true,
  tracking: true,
  cutFlash: true,
  cutClick: true,
};

// Padrão da PRODUÇÃO: os cinco cartões do editor ligados, e a pausa DESLIGADA.
// O desligado é a mudança que este trabalho trouxe — ver o cartão da pausa.
export const PRODUCTION_EDIT_ELEMENTS_DEFAULT: VideoEditorEditElementsChoice = {
  ...EDIT_ELEMENTS_DEFAULT,
  pauses: false,
};

export function EditElementsPanel({
  value,
  onChange,
  achatado = false,
  tema = 'escuro',
  colunas = 'grid-cols-2',
  rotulo = 'Elementos de edição',
  // Cartão da PAUSA entre as falas. Só o VÍDEO POR CENAS tem pausa (é ele que
  // posiciona cada fala num segundo planejado); no editor manual não existe o
  // conceito, então o cartão não é oferecido e o campo nem é lido.
  mostrarPausa = false,
}: {
  value: VideoEditorEditElementsChoice;
  onChange: (patch: Partial<VideoEditorEditElementsChoice>) => void;
  achatado?: boolean;
  tema?: 'escuro' | 'claro';
  colunas?: string;
  rotulo?: string;
  mostrarPausa?: boolean;
}) {
  const claro = tema === 'claro';
  // ── PROJETO ACHATADO: OS CINCO CARTÕES NÃO TÊM ONDE PEGAR ──────────────────
  //
  // Em `sourceMode: 'flattened'` o projeto é UM clipe único que É o MP4 já
  // renderizado — zoom, legenda e tela dividida queimados dentro. Os cinco cartões
  // vinham acesos e o painel era desenhado igual ao do projeto com camadas. MEDIDO
  // num achatado de 26,2s: `tracking` cai em `sanitizeZoomCenter(null)` → centro
  // 0,5 e nenhum pan (inerte); `flash` e `clique` procuram emendas numa timeline de
  // um clipe e recebem `[]` (inertes); e os dois zooms eram PIORES que inertes —
  // aplicavam um recorte novo de 14%→18% sobre o zoom já queimado, comendo a
  // legenda da entrega, e desligar depois não devolvia o quadro perdido. O render
  // agora pula o zoompan neste modo (videoEditorService), e aqui o painel para de
  // prometer o que o arquivo não pode mais receber.
  //
  // O tracking desloca o centro do recorte; sem recorte não há o que deslocar.
  const temZoom = !achatado && (value.zoomCuts || value.zoomAuto);
  const items: Array<{
    key: keyof VideoEditorEditElementsChoice;
    label: string;
    hint: string;
    disabled?: boolean;
  }> = [
    { key: 'zoomCuts', label: 'Zoom in/out nos cortes', hint: achatado ? 'Já está dentro do arquivo' : 'Cada trecho abre num plano diferente do anterior', disabled: achatado },
    { key: 'zoomAuto', label: 'Automação de zoom in', hint: achatado ? 'Já está dentro do arquivo' : 'A imagem fecha devagar ao longo do trecho', disabled: achatado },
    {
      key: 'tracking',
      label: 'Movimento de tracking',
      hint: achatado ? 'Já está dentro do arquivo' : (temZoom ? 'O quadro acompanha onde a pessoa está' : 'Precisa de um dos zooms ligado'),
      disabled: achatado || !temZoom,
    },
    { key: 'cutFlash', label: 'Flash na transição', hint: achatado ? 'Já está dentro do arquivo' : 'Um feixe de luz varre a emenda', disabled: achatado },
    { key: 'cutClick', label: 'Clique no corte', hint: achatado ? 'Já está dentro do arquivo' : 'Efeito curto junto do flash', disabled: achatado },
    // ── A PAUSA é o único cartão que não vem do editor ─────────────────────
    //
    // O editor manual não tem pausa: a régua dele empacota os clipes colados. Ela
    // nasceu no vídeo por cenas, para o arquivo respeitar a duração planejada do
    // brief, e o preço é quadro CONGELADO entre as falas — o avatar para de piscar
    // no meio da frase. Por isso ela vem DESLIGADA por padrão e é escolha explícita.
    ...(mostrarPausa ? [{
      key: 'pauses' as keyof VideoEditorEditElementsChoice,
      label: 'Pausa entre as falas',
      hint: 'Segura o quadro parado até o tempo planejado',
      disabled: false,
    }] : []),
  ];

  return (
    <div>
      <FieldLabel claro={claro}>{rotulo}</FieldLabel>
      <div className={`grid ${colunas} gap-1.5`}>
        {items.map(item => {
          const on = Boolean(value[item.key]) && !item.disabled;
          return (
            <button
              key={item.key}
              type="button"
              disabled={item.disabled}
              aria-pressed={on}
              onClick={() => onChange({ [item.key]: !value[item.key] } as Partial<VideoEditorEditElements>)}
              className={`rounded-lg border p-1.5 text-left transition disabled:opacity-40 ${on
                ? (claro ? 'border-indigo-500 bg-indigo-50' : 'border-indigo-400 bg-indigo-500/15')
                : (claro ? 'border-slate-200 bg-white hover:bg-slate-50' : 'border-white/10 bg-white/[0.03] hover:bg-white/[0.07]')}`}
            >
              <div className="relative mb-1 h-10 w-full overflow-hidden rounded bg-slate-900">
                {item.key === 'zoomCuts' && (
                  <div className="absolute inset-0 flex items-center justify-center gap-0.5">
                    {[5, 8, 6].map((tam, i) => (
                      <Fragment key={i}>
                        {i > 0 && <span className={`h-6 w-px ${on ? 'bg-indigo-300/60' : 'bg-slate-700'}`} />}
                        <span
                          className={`rounded border ${on ? 'border-indigo-300' : 'border-slate-600'}`}
                          style={{ width: `${tam * 1.7}px`, height: `${tam * 2.3}px` }}
                        />
                      </Fragment>
                    ))}
                  </div>
                )}
                {item.key === 'zoomAuto' && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    {[8.5, 6.2, 3.9].map((tam, i) => (
                      <span
                        key={i}
                        className={`absolute rounded border ${i === 0 ? 'border-dashed' : ''} ${on ? 'border-indigo-300' : 'border-slate-600'}`}
                        style={{ width: `${tam * 1.7}px`, height: `${tam * 2.3}px`, opacity: i === 0 ? 0.45 : 1 - (i - 1) * 0.45 }}
                      />
                    ))}
                  </div>
                )}
                {item.key === 'tracking' && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="relative h-8 w-full">
                      <div className="absolute left-[18%] top-1/2 h-4 w-3 -translate-y-1/2 rounded bg-slate-600" />
                      <div className={`absolute left-[14%] top-1/2 h-6 w-5 -translate-y-1/2 rounded border ${on ? 'border-indigo-300' : 'border-slate-600'}`} />
                      <div className="absolute right-[18%] top-1/2 h-4 w-3 -translate-y-1/2 rounded bg-slate-600/50" />
                      <div className={`absolute left-[42%] top-1/2 h-px w-[16%] -translate-y-1/2 ${on ? 'bg-indigo-300/70' : 'bg-slate-700'}`} />
                    </div>
                  </div>
                )}
                {item.key === 'cutFlash' && (
                  <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute inset-0 flex items-stretch">
                      <div className="flex-1 bg-slate-700" />
                      <div className={`w-px ${on ? 'bg-slate-400' : 'bg-slate-700'}`} />
                      <div className="flex-1 bg-slate-800" />
                    </div>
                    <div
                      className={`absolute -top-1/3 left-[38%] h-[170%] w-4 -rotate-[18deg] blur-[3px] ${on ? 'opacity-90' : 'opacity-20'}`}
                      style={{ background: 'linear-gradient(90deg,rgba(255,255,255,0) 0%,rgba(255,255,255,0.95) 50%,rgba(255,255,255,0) 100%)' }}
                    />
                  </div>
                )}
                {item.key === 'cutClick' && (
                  <div className="absolute inset-0 flex items-center justify-center gap-[2px]">
                    {[3, 6, 11, 21, 31, 15, 8, 4, 3].map((h, i) => (
                      <span
                        key={i}
                        className={`w-[2px] rounded-full ${on ? (i === 4 ? 'bg-indigo-300' : 'bg-slate-400') : 'bg-slate-700'}`}
                        style={{ height: `${h}px` }}
                      />
                    ))}
                  </div>
                )}
                {item.key === 'pauses' && (
                  <div className="absolute inset-0 flex items-center justify-center gap-[3px]">
                    {[14, 20, 26, 20, 0, 0, 0, 20, 26, 14].map((h, i) => (
                      <span
                        key={i}
                        className={`w-[2px] rounded-full ${h === 0
                          ? (on ? 'bg-amber-300/80' : 'bg-slate-700')
                          : (on ? 'bg-slate-400' : 'bg-slate-700')}`}
                        style={{ height: `${h || 2}px` }}
                      />
                    ))}
                  </div>
                )}
                <span className={`absolute right-0.5 top-0.5 h-3 w-3 rounded-full border text-[8px] leading-[11px] text-center ${on
                  ? 'border-indigo-300 bg-indigo-500 text-white'
                  : 'border-slate-600 bg-slate-800 text-transparent'}`}
                >
                  ✓
                </span>
              </div>
              <p className={`text-[10px] font-bold leading-tight ${on
                ? (claro ? 'text-indigo-700' : 'text-indigo-200')
                : (claro ? 'text-slate-700' : 'text-slate-300')}`}
              >
                {item.label}
              </p>
              <p className={`mt-0.5 text-[9px] leading-snug ${claro ? 'text-slate-500' : 'text-slate-500'}`}>{item.hint}</p>
            </button>
          );
        })}
      </div>
      <p className="mt-1.5 text-[9px] leading-snug text-slate-500">
        {achatado
          ? 'Este projeto abre como um clipe único: os elementos de edição já estão dentro do arquivo. Ligá-los aqui recortaria o quadro de novo, por cima do que a IA já desenhou.'
          : 'Flash e clique caem nas mesmas emendas — as de virada do vídeo, não todas, para não virar estrobo. Valem na próxima exportação.'}
      </p>
      {temZoom && (
        <p className="mt-1 rounded-lg border border-rose-400/20 bg-rose-500/10 p-1.5 text-[9px] leading-snug text-rose-200">
          <strong>Desligue o zoom em gravação de tela.</strong>{' '}
          {value.zoomCuts
            ? 'O salto de plano recorta de 10% a 22% das bordas em todo o vídeo.'
            : 'A automação de zoom in recorta até 4% das bordas no fim de cada trecho.'}
        </p>
      )}
    </div>
  );
}
